import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ArrowPathIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    ClockIcon,
    StarIcon,
} from "@heroicons/react/24/outline";

import { Button } from "@/components/ui/Button";
import { getQualityAnalytics } from "../api/analyticsApi";
import type { AnalyticsDateRange } from "../types";

const fmtNumber = (value: number | null | undefined) =>
    value == null ? "—" : new Intl.NumberFormat("en-NG").format(value);

const fmtPercentage = (value: number | null | undefined) =>
    value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(1)}%`;

const fmtRating = (value: number | null | undefined) =>
    value == null || !Number.isFinite(value) ? "—" : value.toFixed(1);

const fmtMinutes = (value: number | null | undefined) =>
    value == null || !Number.isFinite(value) ? "—" : `${value.toFixed(1)} min`;

const StatCard = ({
    label,
    value,
    helper,
}: {
    label: string;
    value: string;
    helper?: string;
}) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">{label}</p>
        <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
        {helper ? <p className="mt-1 text-xs text-slate-400">{helper}</p> : null}
    </div>
);

export const QualityAnalyticsRoute = () => {
    const [dateRange, setDateRange] = useState<AnalyticsDateRange>({});

    const { data, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ["analytics-quality", dateRange],
        queryFn: () => getQualityAnalytics(dateRange),
    });

    const stats = data?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <p className="text-sm font-medium text-rose-700 dark:text-rose-300">
                    Failed to load quality analytics
                </p>
            </div>
        );
    }

    if (!stats) {
        return (
            <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center dark:border-slate-800 dark:bg-slate-900">
                <p className="text-sm text-slate-500 dark:text-slate-400">No quality data available.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <CalendarDaysIcon className="h-4 w-4 text-slate-400" />
                    <input
                        type="date"
                        value={dateRange.startDate ?? ""}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value || undefined }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                    <span className="text-sm text-slate-400">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate ?? ""}
                        onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value || undefined }))}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    />
                </div>
                <Button variant="secondary" size="sm" onClick={() => refetch()} disabled={isFetching}>
                    <ArrowPathIcon className={`mr-1.5 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
                    Refresh
                </Button>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <CheckCircleIcon className="h-4 w-4" />
                    Reliability
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Completion Rate" value={fmtPercentage(stats.completionRatePct)} />
                    <StatCard label="Cancellation Rate" value={fmtPercentage(stats.cancellationRatePct)} />
                    <StatCard label="No-show Rate" value={fmtPercentage(stats.noShowRatePct)} />
                    <StatCard label="On-time Arrival Rate" value={fmtPercentage(stats.onTimeArrivalRatePct)} />
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <StarIcon className="h-4 w-4" />
                    Ratings
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Average Driver Rating" value={fmtRating(stats.averageDriverRating)} />
                    <StatCard label="Average Passenger Rating" value={fmtRating(stats.averagePassengerRating)} />
                    <StatCard label="Driver Rating Coverage" value={fmtPercentage(stats.driverRatingCoveragePct)} />
                    <StatCard label="Passenger Rating Coverage" value={fmtPercentage(stats.passengerRatingCoveragePct)} />
                </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <ClockIcon className="h-4 w-4" />
                    Timing & Experience
                </h3>
                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                    <StatCard label="Average Pickup Wait" value={fmtMinutes(stats.averagePickupWaitMinutes)} />
                    <StatCard label="Average Trip Duration" value={fmtMinutes(stats.averageTripDurationMinutes)} />
                    <StatCard label="Average Arrival Delay" value={fmtMinutes(stats.averageArrivalDelayMinutes)} />
                    <StatCard
                        label="No-show Bookings"
                        value={fmtNumber(stats.noShowBookings)}
                        helper="Confirmed bookings that expired as no-show"
                    />
                </div>
            </div>
        </div>
    );
};
